import { Injectable, signal, computed } from '@angular/core';

export interface ITokenUsage {
  input: number;
  output: number;
  total: number;
}

export interface ITagCost {
  tokenUsage: ITokenUsage;
  apiCalls: number;
}

@Injectable({
  providedIn: 'root',
})
export class ConversationCostService {
  private usageByTagSignal = signal<Record<string, ITagCost>>({});

  public totalInputTokens = computed(() => {
    const usage = this.usageByTagSignal();
    return Object.values(usage).reduce((acc, tagCost) => acc + tagCost.tokenUsage.input, 0);
  });

  public totalOutputTokens = computed(() => {
    const usage = this.usageByTagSignal();
    return Object.values(usage).reduce((acc, tagCost) => acc + tagCost.tokenUsage.output, 0);
  });

  public totalTokens = computed(() => {
    const usage = this.usageByTagSignal();
    return Object.values(usage).reduce((acc, tagCost) => acc + tagCost.tokenUsage.total, 0);
  });

  public totalApiCalls = computed(() => {
    const usage = this.usageByTagSignal();
    return Object.values(usage).reduce((acc, tagCost) => acc + tagCost.apiCalls, 0);
  });

  public totalInputCost = computed(() => this.totalInputTokens() * 0.0000001);
  public totalOutputCost = computed(() => this.totalOutputTokens() * 0.0000004);
  public totalCost = computed(() => this.totalInputCost() + this.totalOutputCost());

  public usageByTag = this.usageByTagSignal.asReadonly();

  public updateTokenUsage(tokens: ITokenUsage, tag: string): void {
    this.usageByTagSignal.update((currentUsage) => {
      const newUsage = { ...currentUsage };
      const tagUsage = newUsage[tag] ?? { tokenUsage: { input: 0, output: 0, total: 0 }, apiCalls: 0 };

      tagUsage.tokenUsage.input += tokens.input;
      tagUsage.tokenUsage.output += tokens.output;
      tagUsage.tokenUsage.total += tokens.total;
      tagUsage.apiCalls += 1;

      newUsage[tag] = tagUsage;
      return newUsage;
    });
  }

  public getCostForTag(tag: string) {
    return computed(() => {
      const tagUsage = this.usageByTagSignal()[tag];
      if (!tagUsage) {
        return {
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0,
          apiCalls: 0,
          inputCost: 0,
          outputCost: 0,
          totalCost: 0,
        };
      }
      const inputCost = tagUsage.tokenUsage.input * 0.0000001;
      const outputCost = tagUsage.tokenUsage.output * 0.0000004;
      return {
        inputTokens: tagUsage.tokenUsage.input,
        outputTokens: tagUsage.tokenUsage.output,
        totalTokens: tagUsage.tokenUsage.total,
        apiCalls: tagUsage.apiCalls,
        inputCost,
        outputCost,
        totalCost: inputCost + outputCost,
      };
    });
  }

  public reset(): void {
    this.usageByTagSignal.set({});
  }
}
